'use client'

import React, { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { Button } from '../ui/button'
import { Minus, X, File, Maximize2, Loader2 } from 'lucide-react'
import { cn } from '../lib/utils'
import { z } from 'zod';
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { RootState } from '../../store'
import { useQuery, useMutation } from 'react-query'
import axios from 'src/axios/axios'
import { setNotification } from '../../redux/actions'
import useAuth from 'src/hooks/useAuth'
import { useTranslation } from 'react-i18next'

const emailSchema = z.string().email();

interface SendMailProps extends HTMLAttributes<HTMLDivElement> {
    onClose: () => void;
    document?: any
}

export const RecipientsInput = ({onAddRecepient}:{onAddRecepient:(recipients:string[])=>void}) => {
    const {t} = useTranslation()
    const [recipients, setrecipients] = useState<string[]>([]);
    const input = useRef<HTMLInputElement>(null)

    useEffect(()=>{
        onAddRecepient(recipients)
    }, [recipients])

    function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value.trim();
        if (value === '') return;
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        const value = e.currentTarget.value.trim();
        if (value === '') return;
    
        if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ' || e.key === ',') {
          e.preventDefault();
          if (emailSchema.safeParse(value).success) {
            setrecipients((prevRecipients) => [...prevRecipients, value]);
            input.current!.value = '';
          } 
          else {
            // Handle invalid email address case
            console.log('Invalid email address');
          }
        }
    }

    function handleBlur() {
        const value = input.current!.value.trim();
        if (value === '') return;
    
        if (emailSchema.safeParse(value).success) {
            setrecipients((prevRecipients) => [...prevRecipients, value]);
          input.current!.value = '';
        } else {
          // Handle invalid email address case
          console.log('Invalid email address');
        }
      }

    function deleteRecipient(index: number) {
        setrecipients((prevRecipients) => prevRecipients.filter((_, i) => i !== index));
    }

    return (
        <div className='flex flex-wrap gap-1'>
            {recipients && recipients.map((recipient, index) => {
                return (
                    <div className='flex items-center gap-1 px-2 border rounded-full w-fit' key={index}>
                        <p className='text-base font-medium text-neutral-600'>{recipient}</p>
                        <button onClick={()=>{ deleteRecipient(index) }} className='group'><X className='w-4 text-neutral-600 group-hover:text-neutral-800'/></button>
                    </div>
                )
            })}
            <input 
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                ref={input} className='flex-1 min-w-[50px] text-base focus:outline-none' type="text" placeholder={t('Ontvangers') || ''}
            />
        </div>
    )
}

const SendMail : React.FC<SendMailProps> = ({document, onClose, className, ...props}) => {
    const {t, i18n} = useTranslation()
    const dispatch = useAppDispatch();
    const {auth} = useAuth()

    const [is_minimized, setis_minimized] = useState(false);

    const [isPending, setisPending] = useState(false);
    const [recipients, setrecipients] = useState<string[]>();
    const [subject, setsubject] = useState<string>('');
    const [message, setmessage] = useState<string>('');

    const { data:settings_data } = useQuery({
        queryKey:['email_checksettings'],
        queryFn: async () => {
            const {data:response} = await axios.get(`email/checksettings?token=${auth.data?.token}`)
            if(response.errorcode === 0) { return response.errorcode } 
            else { return response.errorcode || 902}
        }
    })

    async function send() {
        const obj = {
            token: auth.data?.token,
            body: message,
            subject:subject,
            attachments: [
                {
                    file: document?.file,
                    filename: document?.filename || 'Document'
                }
            ],
            recipients: recipients,
            bodyformat: 'HTML',
            sender:'noreply@qastan.be'
        }
        

        try {
            setisPending(true);
            let {data} = await axios.post(`email/send`, obj)
            if(data.errorcode === 0){
                //Success
                setmessage('');
                setsubject('')
                dispatch(setNotification({type:'success', title:'Succes', message:t('Mail verstuurd')}))
                onClose()
            }
            else {
                //Something went wrong
                console.log(obj)
                console.log(data)
                dispatch(setNotification({type:'error', title:'Error', message:t('Mail versturen mislukt')}))
            }
            
        } catch (error) {
            console.log(error)
            dispatch(setNotification({type:'error', title:'Error', message:t('Mail versturen mislukt')}))
            setisPending(false);
        } finally {
            setisPending(false);
        }
    }

    return (
    <>
    <div {...props} className={cn(`${is_minimized && 'hidden'} fixed z-10 bottom-4 right-4 bg-white border overflow-hidden shadow-lg rounded-lg w-96 animate-in fade-in`, className)}>
        <div className='relative'>
            <div className={`${settings_data === 0 && 'hidden'} absolute inset-0 top-12 flex justify-center items-center bg-white/80 backdrop-blur-sm`}>
                [{settings_data}] {(settings_data === 801 || settings_data === 902) ? 'Geen e-mailprofiel geconfigureerd' : 'Er liep iets mis'}
            </div>

            <div className='p-4'>
                <div className='flex items-center justify-between gap-1'>
                    <p className='font-medium'>{t('Nieuw bericht')}</p>
                    <div className='flex gap-1'>
                        <button onClick={()=>{ setis_minimized(true) }} className='flex items-center justify-center w-8 h-8 duration-200 rounded-sm hover:bg-neutral-300'><Minus className='w-4 text-neutral-700'/></button>
                        <button onClick={()=>{ onClose() }} className='flex items-center justify-center w-8 h-8 duration-200 rounded-sm hover:bg-neutral-300 bg-neutral-200'><X className='w-4 text-neutral-700'/></button>
                    </div>
                </div>
                <div className='py-4 border-b'>
                    <RecipientsInput onAddRecepient={(list)=>{ setrecipients(list) }}/>
                </div>
                <div className='flex items-center py-4 border-b'>
                    <input value={subject} onChange={({currentTarget})=>{ setsubject(currentTarget.value) }} maxLength={80} className='w-full focus:outline-none' placeholder={`${t('Onderwerp')}...`}/>
                    <div className='px-1 text-sm rounded-sm bg-neutral-200 text-neutral-500'>{80 - (subject?.length || 0)}</div>
                </div>
                <div className='mt-4'>
                    <textarea value={message} onChange={({currentTarget})=>{ setmessage(currentTarget.value )}} className='w-full resize-none h-52 focus:outline-none' placeholder={`${t('Uw bericht')}...`}/>
                    <div className='flex items-center gap-2'>
                        <div className='rounded-sm border flex justify-center items-center w-8 h-8 min-w-[2rem]'><File className='w-4 text-neutral-400'/></div>
                        <p className='font-semibold truncate'>{document?.filename || 'Document'}</p>
                    </div>
                </div>
            </div>

            <div className='flex justify-end p-4 rounded-b-lg bg-slate-100'>
                <Button disabled={message === '' || subject === '' || recipients?.length === 0 || isPending} onClick={()=>{ send() }}>{!isPending ? t('Versturen') : <Loader2 className='w-4 h-4 animate-spin'/>}</Button>
            </div>
        </div>
    </div>

    <div className={`${!is_minimized && 'hidden'} fixed bottom-0 right-4 bg-white border shadow-lg rounded-t-lg p-2`}>
        <div className='flex items-center justify-between gap-4'>
            <p className='font-medium'>{t('Nieuw bericht')}</p>
            <div className='flex gap-1'>
                <button onClick={()=>{ setis_minimized(false) }} className='flex items-center justify-center w-8 h-8 duration-200 rounded-sm hover:bg-neutral-300'><Maximize2 className='w-4 text-neutral-700'/></button>
                <button onClick={()=>{ onClose() }} className='flex items-center justify-center w-8 h-8 duration-200 rounded-sm hover:bg-neutral-300 bg-neutral-200'><X className='w-4 text-neutral-700'/></button>
            </div>
        </div>
    </div>

    </>
  )
}

export default SendMail